import React from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CalendarDays } from "lucide-react";
import { useScrollReveal } from "../hooks/useScrollReveal";

const EventsPage = () => {
  const navigate = useNavigate();
  const headerReveal = useScrollReveal({ threshold: 0.1 });

  const upcoming = [
    {
      title: "Pool Parties",
      icon: "🐾",
      description:
        "Supervised aquatic gatherings combining safe social play with low-impact exercise for dogs of varying ages and swim abilities.",
    },
    {
      title: "Birthday Celebrations",
      icon: "🎉",
      description:
        "Personalised celebrations featuring themed activities, nutritious treats, and guided group play to make each milestone special.",
    },
    {
      title: "Socialisation Workshops",
      icon: "🐕",
      description:
        "Trainer-led sessions using reward-based methods to build confidence, impulse control, and healthy interactions.",
    },
    {
      title: "Adoption Events",
      icon: "❤️",
      description:
        "In collaboration with local rescues—meet adoptable dogs, learn about responsible rehoming, and support shelter partners.",
    },
  ];

  const reasons = [
    {
      title: "Social development",
      desc: "Builds social skills and healthy interactions through structured, positive group activities.",
    },
    {
      title: "Enrichment",
      desc: "Delivers mental and physical stimulation tailored to individual energy levels and play styles.",
    },
    {
      title: "Community",
      desc: "Fosters connections between owners and builds a supportive local pet community.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col visitor-page">
      <Navbar />

      {/* Hero (gallery style) */}
      <section className="bg-gradient-to-br from-white via-domigo-pink/10 to-domigo-purple/5 py-12 lg:py-20">
        <div
          ref={headerReveal.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${headerReveal.isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-12"
            }`}
        >
          <div className="inline-flex items-center justify-center mb-6">
            <div className="p-4 bg-gradient-pink rounded-3xl shadow-pink">
              <CalendarDays className="text-white" size={48} />
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight mb-4">
            EVENTS
          </h1>
          <h2 className="text-xl text-gray-700 mb-4">Domigo Events — Indore</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Curated, pet-first experiences that enrich wellbeing and strengthen
            the bond between pets and people.
          </p>

          {/* hero CTA removed as requested */}
        </div>
      </section>

      {/* Upcoming Events (cards) */}
      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold mb-6  bg-gradient-to-r from-domigo-purple to-domigo-purple bg-clip-text text-transparent text-center">
            Upcoming Events
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcoming.map((ev, idx) => (
              <div
                key={idx}
                className="bg-white rounded-3xl shadow-soft overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className="h-48 bg-gray-50 flex items-center justify-center text-6xl">
                  <span aria-hidden="true">{ev.icon}</span>
                </div>

                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    {ev.title}
                  </h4>
                  <p className="text-gray-600 mb-6">{ev.description}</p>

                  <button
                    onClick={() => navigate("/contact")}
                    className="w-full bg-gradient-pink text-white py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-300"
                  >
                    Contact for Registration
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Attend */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-domigo-purple to-domigo-purple bg-clip-text text-transparent">
            What Makes This Event Special
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reasons.map((r, i) => (
              <div
                key={i}
                className="group bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <p className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-domigo-purple transition-colors duration-300">
                  {r.title}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {r.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA removed as requested */}

      <Footer />
    </div>
  );
};

export default EventsPage;
